import { useCallback, useEffect } from 'react';

type Id = unknown;

const playEvent = 'mf_videoplay';
declare global {
  interface WindowEventMap {
    [playEvent]: CustomEvent<Id>;
  }
}

/**
 * Manages video playback when multiple videos are on the page. Should be used for inline videos,
 * spotlight videos, and any other video that should pause when another video is played.
 * ---
 * This hook listens for a custom event on the window that is fired by onPlay. When the event is
 * fired, it checks if the video element in the event detail matches the current video. If it
 * doesn't, it calls the onPause callback.
 *
 * @param id Any unique identifier for the video. E.g. a ref or html id
 * @param onPause Callback to run when the video is paused
 * @returns A function to call when the video is played
 */
export function useVideoPlayback(id: Id, onPause: () => void) {
  useEffect(() => {
    // when any video is played, it fires a CustomEvent with the video id (see onPreviewClick). If
    //   the ID doesn't match the current video, pause the video and show the preview. This allows
    //   us to only play one video at a time without using any global state/context.
    const onPlay = (event: WindowEventMap[typeof playEvent]) => {
      if (event.detail !== id) {
        onPause();
      }
    };
    window.addEventListener(playEvent, onPlay);
    return () => window.removeEventListener(playEvent, onPlay);
  }, [id, onPause]);

  return useCallback(() => {
    window.dispatchEvent(new CustomEvent(playEvent, { detail: id }));
  }, [id]);
}
